import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {InfluencerService} from '../../../services/influencer/influencer.service';
import {
  BestPerformingExposure, ContentPieChart,
  GaugeCircleChartData,
  InfluencerDetail, InfluencerPost, PieChartValue,
  PlatformReach, ProgressBar,
  TopExposure,
  ChannelFollowers
} from '../../../../models/influencer';
import {CommonConstant, kpiAttributes, listOrderedPlatforms, pieChartConfig} from '../../../app.constants';
import {ActivatedRoute} from '../../../../../node_modules/@angular/router';
import {UserService} from '../../../services/user.service';
import {Kpi, Platform} from '../../../../models/common';
import {ReferenceDataService} from '../../../services/reference-data/reference-data.service';
import 'rxjs/add/observable/interval';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {DemographicComponent} from '../../influencer/demographic/demographic.component';
import {TranslateService} from '@ngx-translate/core';
import {PostListComponent} from '../../influencer/post-list/post-list.component';
import {Observable} from 'rxjs/Observable';
import { DateUtils } from '../../../utils/date.utils';
import { AppSettings } from '../../../utils/app-settings';

export enum ServiceCounter {
  init = 0,
  max = 10,
  renderTimeout = 500
}

export enum LIMIT {
  LIMIT_10 = 10,
  LIMIT_20 = 20,
}

declare var require: any;

@Component({
  selector: 'influencer-detail-pdf-viewer',
  templateUrl: './influencer-detail-pdf-viewer.component.html',
  styleUrls: ['./influencer-detail-pdf-viewer.component.scss']
})
export class InfluencerDetailPdfViewerComponent implements OnInit {
  public influencer: InfluencerDetail;
  public influencerPostList: Array<object>;
  public topMention: Array<object>;
  public popularTopic: Array<object>;
  public topicAndEngagementChart: Array<object>;
  public groupId: number;
  public influencerId: number;
  public isVisibleTopicChart: boolean;
  public influencerActivityLineChartData: Array<object> | null;
  public influencerKPIData: Kpi | null;
  public platformReachs: any[];
  public platformList: Platform[] = [];
  public topExposures: TopExposure[] = [];
  public bestPerformingExposures: BestPerformingExposure[] = [];
  public listPlatformReach: InfluencerPost[] = [];
  public period: number;
  public currentDate: Date;
  public previousDate: Date;
  public selectedDemographicSource = {
    name: null,
    platformName: null
  };
  public pieChartHeight: number;
  public pieChartCenter = [];
  public pieChartSize: number;
  public pieChartInnerSize: string;
  public pieChartTitleStyling: object;
  public pieChartLegend: object;
  @ViewChild('Demographics')
  public demographics: DemographicComponent;

  public activeChannel: number;
  private channelFollower: number[] = [];

  // channel followers
  public channelFollowersData: GaugeCircleChartData[] = [];
  public isEnableChannelFollowers = false;

  constructor(
    private influencerService: InfluencerService,
    private route: ActivatedRoute,
    private userService: UserService,
    private referenceDataService: ReferenceDataService,
    private pdfExportService: PdfExportService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.isEnableChannelFollowers = AppSettings.isEnableChannelFollowers();
    // disable highchart animation
    const highcharts = require('highcharts');
    highcharts.setOptions({
      chart: {
        animation: false
      },
      plotOptions: {
        series: {
          animation: false
        }
      }
    });
    // load report data
    Observable.combineLatest(
      this.route.params,
      this.route.queryParams
    ).subscribe(([res1, res2]) => {
      this.influencerId = res1.id;
      this.activeChannel = res2.insightChannel ? Number(res2.insightChannel) : null;
      if (res2.insightChannel) {
        this.getChannelFollowers(this.influencerId, this.activeChannel);
      } else if (res2.channelFollower) {
        this.channelFollower = res2.channelFollower.indexOf(',') >= 0 ? res2.channelFollower.split(',').map(Number) : Number(res2.channelFollower);
        this.getChannelFollowers(this.influencerId, this.channelFollower);
      } else {
        this.getChannelFollowers(this.influencerId, null);
      }
      // get topic data
      this.isVisibleTopicChart = false;
      this.influencerService.checkInfluencerCreditAvailability(this.influencerId, this.userService.getCompanyId());
      // load influencer details
      this.getInfluencerDetails(this.influencerId);
      // get post list
      this.getInfluencerPosts(this.influencerId, this.activeChannel);
      this.getPeformanceReportTimeLineChart(this.influencerId, this.activeChannel);
      // load platform reach table data
      this.getPlatformReachTable(this.influencerId, this.activeChannel);
      this.getExposures(this.influencerId, this.activeChannel);
      this.getBestPerforming(this.influencerId, this.activeChannel);
    });
    this.platformList = this.referenceDataService.platforms;
    this.pdfExportService.triggerIncrementReportReady.subscribe((numberReportReady) => {
      if (numberReportReady === ServiceCounter.max && this.influencerService.isCreditAvailable() === true) {
        console.log(' ***************************************** INFLUENCER READY TO EXPORT PDF *****************************************');
        setTimeout(() => {
          this.pdfReady();
          // mark influencer as visited
          this.influencerService.visitInfluencer(this.influencerId, this.userService.getCompanyId()).subscribe();
        }, ServiceCounter.renderTimeout);
      }
    });
    this.currentDate = DateUtils.now();
    this.previousDate = DateUtils.addPerid(this.currentDate, -CommonConstant.PERIOD_30_DAYS);
    this.loadPieChartStyling();
  }

  getSupportedPlatformReachIdList(platformReachs: GaugeCircleChartData[]) {
    return platformReachs.map((pr) => {
      const platform = listOrderedPlatforms.find(p => p.name.toLowerCase() === pr.name.toLowerCase());
      return platform ? platform.id : null;
    });
  }

  private pdfReady () {
    if (window && typeof (<any>window).pdfReady === 'function') {
      setTimeout(() => {
        (<any>window).pdfReady();
      }, ServiceCounter.renderTimeout);
    }
  }

  private getInfluencerDetails(influencerId) {
    this.influencerService.getInfluencer(influencerId).subscribe((data) => {
      this.influencer = data;
      const activeChannel = this.activeChannel && this.activeChannel > 0 ? {
        id: this.activeChannel
      } : null;
      this.influencerKPIData = this.influencerService.normalizeChannelAverageKpi(data.channelAverageKpis, data.average, activeChannel);

      if (data.platformReaches) {
        const calculatedPlatformReachsData = this.influencerService.calculatePlatformReach(data.platformReaches);
        this.platformReachs = calculatedPlatformReachsData.platformReachs;
      }

      this.popularTopic = new Array<object>();
      this.topicAndEngagementChart = new Array<object>();

      if (this.influencer && Array.isArray(this.influencer.topTopics)) {
        this.influencer.topTopics.forEach (topic => {
          if (topic.name !== 'NA' && Math.round(topic.percent * 1000) / 10 !== 0) {
            this.popularTopic.push({ name: topic.name, y: topic.percent });
          }
          if (topic.name !== 'NA' && Math.round(topic.engagement * 1000) / 10 !== 0) {
            this.topicAndEngagementChart.push({ name: topic.name, y: topic.engagement });
          }
        });
      }
      this.isVisibleTopicChart = this.influencer.topTopics != null && this.influencer.topTopics.length > 0;

      // extract top mention list
      this.topMention = new Array<object>();
      if (this.influencer && Array.isArray(this.influencer.topMentions)) {
        this.influencer.topMentions.forEach(mention => {
          this.topMention.push({
            keyword: mention.mention,
            number: mention.count,
          });
        });
      }
    });
  }

  private getInfluencerPosts(influencerId: number, channelId: number) {
    let postListParams = {
      orderBy: kpiAttributes.engagement,
      limit: CommonConstant.LIMIT_20,
    };
    if (channelId && channelId > 0) {
      postListParams = Object.assign({}, postListParams, {
        channelId
      });
    }
    this.influencerService.getInfluencerPosts(influencerId, postListParams)
      .subscribe(
        data => {
          data.forEach(item => {
            item.title  = item.title.replace(/\uFFFD/g, ''); // remove unknown character
            item.title = item.title.substring(0, 100) + '...'; // get 100 characters
          });
          data.sort((a, b) => b.engagement - a.engagement);
          this.influencerPostList = channelId && channelId > 0 ? data : data.slice(0, CommonConstant.LIMIT_20);
          // if no posts, post list pdf module should be activated immediately
          if (this.influencerPostList.length === 0) {
            this.pdfExportService.incrementNumberReportReady(PostListComponent.COMPONENT_NAME);
          }
        },
        (error) => {}
      );
  }

  private getPeformanceReportTimeLineChart(influencerId, channelId) {
    let dailyKpiParam = {
      period: CommonConstant.PERIOD_30_DAYS
    };
    if (channelId) {
      dailyKpiParam = Object.assign({}, dailyKpiParam, {
        channelId
      });
    }
    this.influencerService.getDailyKpiTimeLineChart(influencerId, dailyKpiParam).subscribe((res: any) => {
      this.influencerActivityLineChartData = res;
    });
  }

  private getPlatformReachTable (influencerId: number, channelId: number) {
    let platformReachTableParams = {
      limit: LIMIT.LIMIT_10,
      period: CommonConstant.PERIOD_30_DAYS
    };
    if (channelId) {
      platformReachTableParams = Object.assign({}, platformReachTableParams, {
        channelId
      });
    }
    this.influencerService.getInfluencerPosts(influencerId, platformReachTableParams).subscribe((res) => {
      this.listPlatformReach = res;
    });
  }

  private getExposures (influencerId, channelId) {
    let params = {
      period: CommonConstant.PERIOD_30_DAYS
    };
    if (channelId) {
      params = Object.assign({}, params, {
        channelId
      });
    }
    this.influencerService.getTopExposures(influencerId, params).subscribe((res: any) => {
      this.topExposures = [res.reach, res.engagement, res.impact];
    });
  }

  private getBestPerforming (influencerId, channelId) {
    let bestPerformingParam = {
      period: CommonConstant.PERIOD_30_DAYS,
      limit: LIMIT.LIMIT_10
    };
    if (channelId) {
      bestPerformingParam = Object.assign({}, bestPerformingParam, {
        channelId
      });
    }
    this.influencerService.getBestPerforming(influencerId, bestPerformingParam).subscribe((res: any) => {
      this.bestPerformingExposures = res;
    });
  }

  get logoImage() {
    return this.userService.logoImage;
  }

  getSelectedDemographicSource(evt) {
    this.selectedDemographicSource.name = evt.name;
    this.selectedDemographicSource.platformName = evt.platformName;
  }

  private loadPieChartStyling() {
    this.pieChartHeight = pieChartConfig.pdfView.pieChartHeight;
    this.pieChartCenter = pieChartConfig.pdfView.pieChartCenter;
    this.pieChartSize = pieChartConfig.pdfView.pieChartSize;
    this.pieChartInnerSize = pieChartConfig.pdfView.pieChartInnerSize;
    this.pieChartTitleStyling = pieChartConfig.pdfView.pieChartTitleStyling;
    this.pieChartLegend = pieChartConfig.pdfView.pieChartLegend;
  }

    /**
  * get channel followers of influencer
  * @param influencerId
  * @param activeChannel
  */
  private getChannelFollowers(influencerId, activeChannel) {
    this.influencerService.getChannelFollowerData(influencerId, activeChannel).subscribe(res => {
      if (res) {
        const data = this.influencerService.calculateChannelFollower(res);
        this.channelFollowersData = data.calculatedChannelFollower;
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/main/pdf-viewer/influencer-detail-pdf-viewer/influencer-detail-pdf-viewer.component.ts